import { z } from 'zod';
import { GENDERS } from '../constants/enums';
import { citizenshipSchema, type DictionaryFontWeight, type WorkObjectDisplayStyle } from './dictionaries';
import { PHONE_FIELDS, PHONE_RAW_MAX_LENGTH, type CandidatePhoneCountry, type PhoneRawField, type PhoneQueryField, type PhoneMatchField } from '../constants/phone-columns';
import { COLORABLE_COLUMNS, EDITABLE_COLUMNS, GROUPABLE_FIELD_KEYS } from '../constants/columns';
import { clientOpIdSchema, filterSchema, isoDateSchema, paginationSchema, sortSchema, uuidSchema, type Paginated } from './common';
import { isValidInternationalPhoneList } from '../utils/international-phone';
import { blacklistCheckSchema } from './blacklist';

/** Телефон: либо пусто, либо распознаваемый номер указанной страны. */
/**
 * Телефонное поле: один номер или несколько.
 *
 * У человека их бывает под десяток — свой, российский, рабочий, номер жены,
 * номер общежития. Раньше в поле помещался ровно один: второй записывали
 * через запятую, и для поиска он переставал существовать. Теперь номера
 * разделяются переводом строки (Alt+Enter в ячейке, как в Excel), запятой
 * или точкой с запятой, и каждый идёт в поиск и в проверку дублей.
 */
const phoneField = (hint: CandidatePhoneCountry) =>
  z
    .string()
    .trim()
    .max(PHONE_RAW_MAX_LENGTH, `Телефоны — не более ${PHONE_RAW_MAX_LENGTH} символов`)
    .nullish()
    .refine((v) => !v || isValidInternationalPhoneList(v, hint), {
      message:
        hint === 'BY'
          ? 'Один из номеров не похож на белорусский'
          : hint === 'RU'
            ? 'Один из номеров не похож на российский'
            : hint === 'OTHER'
              ? 'Укажите номера в международном формате с кодом страны'
              : 'Один из номеров не соответствует выбранной стране',
    });

const candidatePhoneShape = Object.fromEntries(
  PHONE_FIELDS.map(({ rawField, country }) => [rawField, phoneField(country)]),
) as Record<PhoneRawField, ReturnType<typeof phoneField>>;
const rawPhoneValue = z.string().nullable();
const candidateRawPhoneShape = Object.fromEntries(
  PHONE_FIELDS.map(({ rawField }) => [rawField, rawPhoneValue]),
) as Record<PhoneRawField, typeof rawPhoneValue>;
const duplicatePhoneValue = z.string().trim().max(PHONE_RAW_MAX_LENGTH).optional();
const duplicatePhoneShape = Object.fromEntries(
  PHONE_FIELDS.map(({ rawField }) => [rawField.slice(0, -3), duplicatePhoneValue]),
) as Record<PhoneQueryField, typeof duplicatePhoneValue>;

const birthDateField = isoDateSchema
  .nullish()
  .refine(
    (v) => {
      if (!v) return true;
      const d = new Date(v);
      const now = new Date();
      const age = (now.getTime() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
      return age >= 16 && age <= 80;
    },
    { message: 'Возраст вне диапазона 16–80 лет' },
  );

/**
 * Поля кандидата, доступные для записи.
 * Порядок и состав соответствуют CANDIDATE_COLUMNS.
 */
export const candidateWritableSchema = z.object({
  sectionId: uuidSchema.nullish(),
  responsibleUserId: z.string().min(1).max(128).nullish(),
  travelGroupId: uuidSchema.nullish(),

  /** Вставленная строка может оставаться пустой до заполнения ФИО. */
  fullName: z.string().trim().min(3, 'Укажите ФИО').max(255, 'ФИО — не более 255 символов').or(z.literal('')),
  lastName: z.string().trim().max(100).nullish(),
  firstName: z.string().trim().max(100).nullish(),
  middleName: z.string().trim().max(100).nullish(),

  ...candidatePhoneShape,

  birthDate: birthDateField,
  citizenship: citizenshipSchema.nullish(),
  /** Дополнительные гражданства, без повторения основного. */
  citizenshipExtra: z.array(citizenshipSchema).max(12).optional(),
  gender: z.enum(GENDERS).nullish(),

  communicationRaw: z.string().trim().max(255, 'Где общение — не более 255 символов').nullish(),
  cityId: uuidSchema.nullish(),
  /** Дополнительные города, без повторения основного. */
  cityIdsExtra: z.array(uuidSchema).max(20).optional(),
  statusId: uuidSchema.nullish(),
  sourceId: uuidSchema.nullish(),
  projectId: uuidSchema.nullish(),

  workNote: z.string().trim().max(2000, 'Объект работы и смены — не более 2000 символов').nullish(),
  workObjectId: uuidSchema.nullish(),
  workObjectNote: z.string().trim().max(2000, 'Дополнение к объекту — не более 2000 символов').nullish(),
  shiftsCount: z.coerce.number().int().min(0).max(999).nullish(),
  isBooked: z.boolean().nullish(),
  plannedDepartureDate: isoDateSchema.nullish(),
  actualDepartureDate: isoDateSchema.nullish(),

  passportSeries: z.string().trim().max(16).nullish(),
  passportNumber: z.string().trim().max(32).nullish(),
  passportIssuedBy: z.string().trim().max(500).nullish(),
  passportIssueDate: isoDateSchema.nullish(),
  passportPersonalNumber: z.string().trim().max(32).nullish(),
  passportDivisionCode: z.string().trim().max(16).nullish(),
  registrationAddress: z.string().trim().max(500).nullish(),

  inn: z.string().trim().max(16).nullish(),
  snils: z.string().trim().max(16).nullish(),
  hasMedicalBook: z.boolean().nullish(),

  comment: z.string().trim().max(5000, 'Комментарий — не более 5000 символов').nullish(),
  addedDate: isoDateSchema.nullish(),
  tagIds: z.array(uuidSchema).max(20).nullish(),
});

export type CandidateWritable = z.infer<typeof candidateWritableSchema>;

export const candidateCreateSchema = candidateWritableSchema.partial().extend({
  fullName: candidateWritableSchema.shape.fullName,
  /** Попутчики выбираются в черновике и присоединяются при создании. */
  travelMateIds: z.array(uuidSchema).max(50)
    .refine((ids) => new Set(ids).size === ids.length, 'Попутчики не должны повторяться')
    .optional(),
  clientOpId: clientOpIdSchema.optional(),
  /** Явно подтвердить создание, когда система нашла дубль по телефону */
  ignoreDuplicates: z.boolean().default(false),
  /**
   * Явно подтвердить создание, когда человек нашёлся в чёрном списке.
   * Отдельно от дублей: это разные вопросы и разные решения. Дубль значит
   * «его уже заводили», чёрный список — «с ним не работаем».
   */
  ignoreBlacklist: z.boolean().default(false),
  /** Подтвердить создание после просмотра комментариев прежних заявок. */
  ignoreComments: z.boolean().default(false),
});
export type CandidateCreate = z.infer<typeof candidateCreateSchema>;

/**
 * Вставка строки на конкретное место — «как в Excel».
 *
 * Обычное заведение кандидата кладёт строку в конец: у неё следующий
 * порядковый номер, и в списке она всплывает наверх до перезагрузки.
 * Но в таблице, из которой сюда переехали, строки вставляли между
 * соседями — и это осмысленно: люди одного дня и одной машины должны
 * стоять рядом, а не разъезжаться по номерам.
 */
export const candidateInsertSchema = candidateCreateSchema.omit({ travelMateIds: true }).extend({
  /** Номер строки, относительно которой вставляем */
  anchorRowNumber: z.number().int().positive(),
  /** Выше неё или ниже */
  position: z.enum(['above', 'below']),
});
export type CandidateInsert = z.infer<typeof candidateInsertSchema>;

/**
 * Перемещение строки перетаскиванием.
 *
 * Место задаётся СОСЕДОМ, а не новым порядком группы. Под фильтром,
 * под серверным поиском и на недогруженной странице видимые соседи —
 * не настоящие: между ними лежат строки, которых на экране нет. Клиент,
 * приславший «вот новый порядок группы», затёр бы их. Сосед же однозначен
 * при любой выборке, а поворот считает сервер по полной группе.
 *
 * `anchorId: null` — бросок на заголовок группы: в начало или в конец.
 * `groupKey: null` — группа «Не заполнено» либо группировки нет вовсе.
 */
const departureGroupStateSchema = z.object({
  sectionId: uuidSchema.nullable(),
  plannedDepartureDate: isoDateSchema.nullable(),
});
export type DepartureGroupState = z.infer<typeof departureGroupStateSchema>;

export const candidateMoveSchema = z.object({
  movedId: uuidSchema,
  anchorId: uuidSchema.nullable(),
  position: z.enum(['before', 'after']),
  groupBy: z.enum(GROUPABLE_FIELD_KEYS as [string, ...string[]]).nullable(),
  groupKey: z.string().max(255).nullable(),
  /** Версия перетаскиваемой строки — оптимистичная блокировка */
  version: z.number().int().nonnegative(),
  clientOpId: clientOpIdSchema,
  /** Отмена переносит строку обратно; сервер меняет только поле выбранной группы. */
  history: z.object({
    expected: departureGroupStateSchema,
    values: departureGroupStateSchema,
  }).optional(),
});
export type CandidateMove = z.infer<typeof candidateMoveSchema>;

/**
 * Правка одной ячейки — самый горячий эндпоинт системы.
 * Клиент присылает поле, новое значение и версию строки, которую он видел.
 * Если версия разошлась — сервер возвращает 409 с актуальным значением,
 * и грид показывает «эту строку уже изменил другой пользователь».
 */
export const cellPatchSchema = z.object({
  field: z.enum(EDITABLE_COLUMNS as [string, ...string[]]),
  value: z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.string())]),
  /** Версия строки на момент начала правки */
  version: z.number().int().nonnegative(),
  clientOpId: clientOpIdSchema,
});
export type CellPatch = z.infer<typeof cellPatchSchema>;

export const candidateUpdateSchema = candidateWritableSchema.partial().extend({
  version: z.number().int().nonnegative(),
  clientOpId: clientOpIdSchema.optional(),
});
export type CandidateUpdate = z.infer<typeof candidateUpdateSchema>;

/** Объект и его свободное дополнение сохраняются одной правкой ячейки. */
export const candidateWorkObjectValueSchema = z.object({
  workObjectId: uuidSchema.nullable(),
  workObjectNote: candidateWritableSchema.shape.workObjectNote.unwrap().unwrap().nullable(),
});
export const candidateWorkObjectPatchSchema = candidateWorkObjectValueSchema.extend({
  version: z.number().int().nonnegative(),
  clientOpId: clientOpIdSchema,
});
export type CandidateWorkObjectPatch = z.infer<typeof candidateWorkObjectPatchSchema>;
export interface CandidateWorkObjectValue {
  workObjectId: string | null;
  workObjectNote: string | null;
}
export interface CandidateWorkObjectPatchResult {
  id: string;
  version: number;
  updatedAt: string;
  before: CandidateWorkObjectValue;
  after: CandidateWorkObjectValue;
  derived: Record<string, unknown>;
}

/** Массовая правка выделенных строк: одно поле — много строк. */
export const bulkUpdateSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(1000),
  patch: candidateWritableSchema.partial(),
  clientOpId: clientOpIdSchema,
  /** Применять даже к строкам, изменённым после начала операции */
  force: z.boolean().default(false),
});
export type BulkUpdate = z.infer<typeof bulkUpdateSchema>;

export const bulkDeleteSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(1000),
  clientOpId: clientOpIdSchema,
});

/** Скрытие строк общее для всей команды, удаление остаётся отдельным действием. */
export const candidateVisibilitySchema = z.object({
  ids: z.array(uuidSchema).min(1).max(1000),
  hidden: z.boolean(),
  scope: z.enum(['personal', 'all']).default('all'),
  clientOpId: clientOpIdSchema,
});
export type CandidateVisibility = z.infer<typeof candidateVisibilitySchema>;

/**
 * Заливка выделенного — прямоугольник, а не список ячеек.
 *
 * Выделение в гриде и есть прямоугольник: строки × колонки. Список пар
 * «строка-поле» на диапазоне 500×10 повёз бы по сети пять тысяч объектов
 * ради того, что описывается двумя массивами.
 *
 * `color: null` — снять заливку.
 */
export const bulkCellColorSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(1000),
  fields: z.array(z.enum(COLORABLE_COLUMNS as [string, ...string[]])).min(1).max(60),
  color: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, 'Цвет задаётся шестью шестнадцатеричными знаками: #RRGGBB')
    .nullable(),
  clientOpId: clientOpIdSchema,
});
export type BulkCellColor = z.infer<typeof bulkCellColorSchema>;

/** Жирное начертание выделенных ячеек; null возвращает исходный стиль. */
export const bulkCellBoldSchema = bulkCellColorSchema.omit({ color: true }).extend({
  bold: z.boolean().nullable(),
});
export type BulkCellBold = z.infer<typeof bulkCellBoldSchema>;
export interface CandidateCellBoldSnapshot {
  id: string;
  values: Record<string, boolean | null>;
}

/** Заливка всей строки не перезаписывает цвета отдельных ячеек. */
export const bulkRowColorSchema = bulkCellColorSchema.omit({ fields: true });
export type BulkRowColor = z.infer<typeof bulkRowColorSchema>;

/**
 * Вставка блока из Excel (Ctrl+V). Клиент присылает разобранный TSV:
 * какие колонки и какие строки. Строки без id создаются как новые.
 */
/**
 * Отметка о копировании в буфер.
 *
 * Заказчик просил видеть, «кто что копирует»: вынос паспортных данных
 * из базы — это то, ради чего журнал и заводится. Поэтому клиент сообщает
 * серверу только объём и состав выделения, но НЕ значения. Хранить
 * скопированные номера паспортов в журнале означало бы разложить их
 * в ещё одном месте — ровно то, от чего защищаемся.
 */
export const copyLogSchema = z.object({
  /** Ключи скопированных колонок */
  columns: z.array(z.string().max(64)).min(1).max(60),
  /** Сколько строк попало в выделение */
  rows: z.number().int().min(1).max(100_000),
  /** Сколько ячеек всего — строки × колонки, считает клиент */
  cells: z.number().int().min(1).max(1_000_000),
  /** Кандидаты в выделении: до сотни, дальше смысла в перечислении нет */
  candidateIds: z.array(uuidSchema).max(100).default([]),
});
export type CopyLogPayload = z.infer<typeof copyLogSchema>;

export const pasteSchema = z.object({
  /** Ключи колонок в порядке столбцов вставляемого блока */
  columns: z.array(z.enum(EDITABLE_COLUMNS as [string, ...string[]])).min(1).max(60),
  rows: z
    .array(
      z.object({
        id: uuidSchema.nullable(),
        version: z.number().int().nonnegative().nullable(),
        values: z.array(z.union([z.string(), z.null()])),
        /** Буфер CRM сохраняет объект и текст отдельно, без разбора подписи. */
        workObject: candidateWorkObjectValueSchema.optional(),
      }),
    )
    .min(1)
    .max(1000),
  clientOpId: clientOpIdSchema,
});
export type PastePayload = z.infer<typeof pasteSchema>;

export const candidateListQuerySchema = paginationSchema.extend({
  /** Полная выборка грида; без mode сохраняется обычная постраничная выдача. */
  mode: z.enum(['page', 'all']).optional(),
  sort: z.array(sortSchema).max(3).default([{ field: 'rowNumber', dir: 'asc' }]),
  filters: z.array(filterSchema).max(20).default([]),
  /** Глобальный поиск по ФИО, телефонам и комментарию */
  search: z.string().trim().max(100, 'Поиск — не более 100 символов').optional(),
  includeDeleted: z.boolean().default(false),
  /** Показывать скрытые строки рядом с обычными, чтобы их можно было вернуть. */
  includeHidden: z.boolean().default(false),
  /** Отдельный режим восстановления, независимо от сохранённых фильтров. */
  hiddenOnly: z.boolean().default(false),
  /** Только строки указанной группы выезда — для действия «показать всю группу» */
  travelGroupId: uuidSchema.optional(),
}).superRefine((query, ctx) => {
  if (query.mode !== 'all') return;
  if (query.cursor !== undefined) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['cursor'], message: 'Полная загрузка не использует курсор',
  });
  if (query.direction === 'backward') ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['direction'], message: 'Полная загрузка не использует обратную догрузку',
  });
});
export type CandidateListQuery = z.infer<typeof candidateListQuerySchema>;

/** Проверка дублей перед созданием (решение Р-09). */
export const duplicateCheckSchema = z.object({
  ...duplicatePhoneShape,
  fullName: z.string().trim().max(255).optional(),
  birthDate: isoDateSchema.optional(),
  excludeId: uuidSchema.optional(),
});
export type DuplicateCheck = z.infer<typeof duplicateCheckSchema>;

export const duplicateMatchSchema = z.object({
  id: uuidSchema,
  rowNumber: z.number().int(),
  fullName: z.string(),
  ...candidateRawPhoneShape,
  statusName: z.string().nullable(),
  statusColor: z.string().nullable(),
  responsibleName: z.string().nullable(),
  addedDate: z.string().nullable(),
  /** По какому признаку совпало */
  matchedBy: z.enum([
    'name_birthdate',
    ...PHONE_FIELDS.map(({ rawColumn }) => rawColumn.slice(0, -4) as PhoneMatchField),
  ]),
});
export type DuplicateMatch = z.infer<typeof duplicateMatchSchema>;

/** Комментарии прежних заявок сверяются по тем же ФИО и телефонам, что ЧС. */
export const candidateCommentCheckSchema = blacklistCheckSchema;
export type CandidateCommentCheck = z.infer<typeof candidateCommentCheckSchema>;

export const candidateCommentMatchSchema = z.object({
  id: uuidSchema,
  fullName: z.string(),
  comment: z.string(),
  matchedBy: z.enum(['phone', 'name']),
  createdAt: z.string(),
});
export type CandidateCommentMatch = z.infer<typeof candidateCommentMatchSchema>;

/** Форма строки, как её отдаёт API гриду. */
export interface CandidateRow extends Record<PhoneRawField, string | null> {
  sectionId?: string | null;
  sectionLabel?: string | null;
  id: string;
  rowNumber: number;
  version: number;
  separatorAfter: boolean;
  rowColor: string | null;

  responsibleUserId: string | null;
  responsibleName: string | null;
  responsibleInitials: string | null;
  responsibleColor: string | null;

  travelGroupId: string | null;
  travelGroupName: string | null;
  travelGroupColor: string | null;
  travelGroupTextColor?: string | null;
  travelGroupFontWeight?: DictionaryFontWeight | null;

  fullName: string;
  lastName: string | null;
  firstName: string | null;
  middleName: string | null;

  birthDate: string | null;
  /** Полных лет на дату чтения; не хранится и не редактируется отдельно. */
  age?: number | null;
  citizenship: string | null;
  citizenshipExtra?: string[];
  gender: string | null;

  communicationRaw: string | null;

  cityId: string | null;
  cityName: string | null;
  cityColor: string | null;
  cityTextColor?: string | null;
  cityFontWeight?: DictionaryFontWeight | null;
  cityIdsExtra?: string[];
  cityNamesExtra?: string[];

  statusId: string | null;
  statusName: string | null;
  statusColor: string | null;
  statusFontWeight?: DictionaryFontWeight | null;
  statusTextColor: string | null;

  workNote: string | null;
  workObjectId: string | null;
  /** Отдельный текст рядом с объектом. Старые ответы могут его ещё не содержать. */
  workObjectNote?: string | null;
  workObjectName: string | null;
  /** Местоположение сохраняется и для закрытых объектов, которых нет в селекте. */
  workObjectRegion?: string | null;
  workObjectSelectLabel?: string | null;
  workObjectColor?: string | null;
  workObjectDisplayStyle?: WorkObjectDisplayStyle | null;
  workObjectQualifier?: string | null;
  workObjectTextColor?: string | null;
  workObjectFontWeight?: DictionaryFontWeight | null;
  shiftsCount: number | null;
  isBooked: boolean | null;
  plannedDepartureDate: string | null;
  actualDepartureDate: string | null;

  passportSeries: string | null;
  passportNumber: string | null;
  passportIssuedBy: string | null;
  passportIssueDate: string | null;
  passportPersonalNumber: string | null;
  passportDivisionCode: string | null;
  registrationAddress: string | null;

  inn: string | null;
  snils: string | null;
  hasMedicalBook: boolean | null;

  sourceId: string | null;
  sourceName: string | null;
  sourceTextColor?: string | null;
  sourceFontWeight?: DictionaryFontWeight | null;
  projectId: string | null;
  projectName: string | null;
  projectColor: string | null;
  projectTextColor?: string | null;
  projectFontWeight?: DictionaryFontWeight | null;
  tags: Array<{ id: string; name: string; color: string; textColor?: string | null; fontWeight?: DictionaryFontWeight | null }>;
  comment: string | null;

  documentsCount: number;
  hasPassportScan: boolean;

  /**
   * Ручная заливка ячеек: поле → цвет. Пустой объект у подавляющего
   * большинства строк, поэтому поле именно такое, а не колонка на каждый
   * возможный ключ.
   *
   * Наблюдателю сюда не попадают паспортные поля (Р-11): колонок он
   * не видит, и знать, что в них что-то отмечено цветом, ему тоже незачем.
   */
  cellColors: Record<string, string>;
  /** Ручное начертание сильнее справочника: true — жирное, false — обычное. */
  cellBold: Record<string, boolean>;

  addedDate: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
  updatedByName: string | null;
  deletedAt: string | null;
  hiddenAt: string | null;
  hiddenForMe: boolean;
}

/** Безопасный результат присоединения попутчика при создании другого кандидата. */
export type CandidateTravelMateChange = Pick<CandidateRow,
  'id' | 'version' | 'travelGroupId' | 'travelGroupName' | 'travelGroupColor'
  | 'travelGroupTextColor' | 'travelGroupFontWeight' | 'updatedAt' | 'updatedBy' | 'updatedByName'> & {
    /** Снимок до транзакции нужен для одной отмены всего создания. */
    oldTravelGroupId: string | null;
  };

/** Место скрытых строк в исходном порядке листа «2026». */
export interface HiddenRowGap {
  /** Соседние видимые строки внутри той же секции; null — край секции. */
  beforeRowId: string | null;
  afterRowId: string | null;
  sectionId: string | null;
  hiddenCount: number;
  firstRowNumber: number;
  lastRowNumber: number;
  /** Один id может быть в обоих списках, если строка скрыта двумя способами. */
  personalIds: string[];
  globalIds: string[];
}

export interface CandidateListResult extends Paginated<CandidateRow> {
  /** В прочих режимах (поиск, фильтр, сортировка, страница) всегда пусто. */
  hiddenGaps: HiddenRowGap[];
}
